// The scene for any theme: a built-in place's own, or one composed from a made
// theme's description. Composed scenes are kept per theme version.

import type { ThemeId } from "../../shared/types";
import { backgroundThumb } from "./backgrounds";
import { composeScene } from "./compose";
import { SCENES, type Scene } from "./scenes";
import type { ThemeDef } from "./themes";

const made = new Map<string, Scene>();

function seedOf(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return (h >>> 0) % 100000;
}

export function sceneOf(def: ThemeDef): { key: string; scene: Scene } {
  if (Object.hasOwn(SCENES, def.id)) return { key: def.id, scene: SCENES[def.id as ThemeId] };
  const key = `${def.id}:${def.custom?.updatedAt ?? 0}`;
  let scene = made.get(key);
  if (!scene) {
    scene = composeScene(def.scene, { dark: def.mode === "dark", seed: seedOf(def.id) });
    made.set(key, scene);
  }
  return { key, scene };
}

// A still of any theme's scene, small, for pickers and cards. Drawn once and kept.
const thumbs = new Map<string, string>();
export function themeThumb(def: ThemeDef, w = 168, h = 96): string {
  // a theme with pictures shows its first one, which the browser keeps once loaded
  const pic = def.custom?.backgrounds?.[0];
  if (pic) return backgroundThumb(pic.key);
  const { key, scene } = sceneOf(def);
  const k = `${key}:${w}x${h}`;
  const hit = thumbs.get(k);
  if (hit) return hit;
  const cw = scene.pixel ? Math.round(w / 2) : w * 2;
  const ch = scene.pixel ? Math.round(h / 2) : h * 2;
  const c = document.createElement("canvas");
  c.width = cw;
  c.height = ch;
  const ctx = c.getContext("2d")!;
  ctx.imageSmoothingEnabled = !scene.pixel;
  try {
    const st = scene.build(cw, ch);
    scene.frame({ ctx, w: cw, h: ch, t: 3 }, st);
  } catch {
    /* a blank thumb beats a broken picker */
  }
  const url = c.toDataURL("image/png");
  thumbs.set(k, url);
  return url;
}
